import {
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subscription, finalize } from 'rxjs';
import { ColumnDef } from '@3ddv/software-division-components/backoffice/table';
import { ButtonComponent } from '@3ddv/software-division-components/generic/button';
import { GsTableComponent } from '../../shared/ui/table/gs-table.component';
import {
  AppDialogService,
  DialogCloseReason,
} from '../layout/dialog/app-dialog.service';
import { PaginationComponent } from '../layout/pagination/pagination.component';
import { CreateComponent } from './modals/create/create.component';
import { DeleteComponent } from './modals/delete/delete.component';
import { EditComponent } from './modals/edit/edit.component';
import { Upgrade } from './models/upgrade.model';
import { UpgradeService } from './services/upgrade.service';
import { AuthStore } from '../../core/stores/auth.store';
import { buildUpgradeColumns } from './upgrade.columns';

@Component({
  selector: 'app-upgrade',
  templateUrl: './upgrade.component.html',
  styleUrls: ['./upgrade.component.css'],
  imports: [ButtonComponent, GsTableComponent, PaginationComponent],
})
export class UpgradeComponent implements OnInit, OnDestroy {
  private dialogService = inject(AppDialogService);
  protected readonly authStore = inject(AuthStore);
  private activatedRoute = inject(ActivatedRoute);
  private upgradeService = inject(UpgradeService);
  private router = inject(Router);

  public loading = signal(false);

  public data: Array<Upgrade>;
  public totalItemsInDb: number;
  public itemsPerPage: number;
  private subscriptions: Array<Subscription>;
  public currentPage: number;
  public columns: ColumnDef<Upgrade>[] = [];
  @ViewChild('pagination') pagination: PaginationComponent;

  constructor() {
    this.data = [];
    this.subscriptions = [];
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(o => o.unsubscribe());
  }
  ngOnInit(): void {
    this.columns = buildUpgradeColumns(
      upgrade => this.toggleActive(upgrade),
      upgrade => this.openModalEdit(upgrade),
      upgrade => this.openModalDelete(upgrade.id),
      this.authStore.canEdit()
    );

    let resolved = this.activatedRoute.snapshot.data['api'];
    this.data = resolved.data;
    this.totalItemsInDb = resolved.metadata.total;
    this.itemsPerPage = resolved.metadata.perPage;

    const queryparams$ = this.activatedRoute.queryParams.subscribe(params => {
      let resolved = this.activatedRoute.snapshot.data['api'];

      if (!params['page'] || params['page'] > resolved.metadata.lastPage) {
        this.router.navigate(['.'], {
          relativeTo: this.activatedRoute,
          queryParams: { page: 1 },
        });
      }

      if (params['page']) {
        this.currentPage = params['page'];
        this.data = resolved.data;
        this.totalItemsInDb = resolved.metadata.total;
        this.itemsPerPage = resolved.metadata.perPage;
        if (this.pagination) {
          this.pagination.refresh(
            this.currentPage,
            this.totalItemsInDb,
            this.itemsPerPage
          );
        }
      }
    });
    this.subscriptions.push(queryparams$);
  }

  public openModalDelete(upgradeId: number) {
    const { closed$ } = this.dialogService.open(DeleteComponent, {
      size: 'md',
      inputs: { id: upgradeId },
    });
    this.refreshListOnSuccess(closed$, 1);
  }
  public openModalEdit(item: Upgrade) {
    const { closed$ } = this.dialogService.open(EditComponent, {
      size: 'lg',
      inputs: { item: JSON.parse(JSON.stringify(item)) },
    });
    this.refreshListOnSuccess(closed$, this.currentPage);
  }

  public openModalCreate() {
    const { closed$ } = this.dialogService.open(CreateComponent, {
      size: 'lg',
    });
    this.refreshListOnSuccess(closed$, 1);
  }

  public toggleActive(item: Upgrade) {
    const updated: Upgrade = { ...item, active: !item.active };
    this.data = this.data.map(upgrade =>
      upgrade.id === item.id ? updated : upgrade
    );
    const update$ = this.upgradeService.update$(updated, []).subscribe(res => {
      console.log(res);
    });
    this.subscriptions.push(update$);
  }

  private refreshListOnSuccess(
    closed$: Observable<DialogCloseReason | undefined>,
    page: string | number
  ) {
    const onClose$ = closed$.subscribe(reason => {
      if (reason === 'success') {
        this.getall(page);
      }
    });
    this.subscriptions.push(onClose$);
  }
  private getall(page?) {
    if (!page) page = this.currentPage;

    this.loading.set(true);
    const all$ = this.upgradeService
      .getAll$(page)
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (res: any) => {
          this.data = res.data;
          this.totalItemsInDb = res.metadata.total;
          this.itemsPerPage = res.metadata.perPage;
          if (this.pagination) {
            this.pagination.refresh(
              this.currentPage,
              this.totalItemsInDb,
              this.itemsPerPage
            );
          }
          if (this.currentPage > res.metadata.lastPage) {
            this.currentPage = page;
          }
          this.router.navigate(['/private/upgrade'], {
            queryParams: { page: this.currentPage },
          });
        },
        error: err => {
          console.log(err);
        },
      });
    this.subscriptions.push(all$);
  }
}
